/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import root from 'window-or-global';
import { Drawer } from '@/components/Drawer/Drawer';
import { useLabels } from '@/hooks/useLabels';
import NotificationBlock from '@/components/AZCustomComponent/NotificationBlock/NotificationBlockV2';
import { DealsLoader } from '../../lib/components/DealsLoader';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import { resetDiscounts } from '@/actions/itemQuantity';
import type { Deal } from '../../interface';
import { skuDealDetailsQuery, useDealSkuDetails } from '../../api/getDealSkuDetails';
import { dealListQuery, useDealList } from '../../api/getDealList';
import { routePaths } from '@/constants/routePaths';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { eventConstants, eventTypes } from '@/constants/event';
import type { ReduxState } from '@/types';
import { BundleDealV2 } from '../../lib/components/BundleDealV2';
import { BundleDealVehicleSelection } from '../../lib/components/VehicleSelection';
import { NavBar } from '@/components/NavBar';
import { AutoFulfillmentSelector } from '../../lib/components/AutoFulfillmentSelector';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import styles from '../../lib/components/styles.module.scss';
import { clickTrack } from '@/utils/analytics/clickTrack';
import type { CustomLinkName } from '@/types/analytics';
import { useFeatureFlag } from '@/features/globalConfig';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { getErrorCode, getErrorCodeDescription } from '@/utils/errorsHandling';
import { MINI_CART_TITLE_ID, MiniCartFlyoutComp } from '@/features/miniCartFlyout';
import { removeDataPart } from '@/actions/shelf/shelf';
import { DealDrawerConfirmation } from '../../lib/components/DealDrawerConfirmation';
import { trackInStockNearYouLinkClick } from '@/utils/analytics/track/trackInStockNearYouLinkClick';
import { usePageType } from '@/features/category/api/getPageType';
import { VehicleNotFit } from '../../lib/components/DealDrawerErrors/VehicleNotFit';
import { useQueryClient } from '@tanstack/react-query';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '../../hooks/useAcesDealsVehicleAttributes';
import AZSnackBar from '@/components/AZCustomComponent/AZSnackBar';
import { isServer } from '@/utils/isServer';
import { BundleDealOptionList } from '../../lib/components/BundleDealOptionList/BundleDealOptionsV2';

const labelMap = {
  label_deal_bundle_unexpected_error: 'label_deal_bundle_unexpected_error',
  label_deal_doesnt_fit_vehicle: 'label_deal_doesnt_fit_vehicle',
  label_deal_doesnt_fit_vehicle_cta: 'label_deal_doesnt_fit_vehicle_cta',
  lblDealAddedToCart: 'label_deal_added_to_cart',
};

type Props = {
  setShowYmme: (showYmme: boolean) => void;
  showYmme: boolean;
  open: boolean;
  skuId: string | undefined;
  skuPrice?: number;
  skuRetailPrice: string | number | string[] | number[];
  skuFulfillmentTypeId: number | string | null | undefined;
  skuQuantity: number | string;
  skuIsVehicleSpecific: boolean | undefined;
  closeDealsDrawer: () => void;
  handleDealsAddToCart?: () => void;
  dealsAddedToCart?: boolean;
  brand: string | undefined;
  analyticsPageType: string;
  onContinueShopping?: () => void;
  dealConfigurableLabel: string | null | undefined;
  fittingPartsURL: string | undefined;
  partGroupId?: string;
  partTerminologyId?: string;
  partTypeId: string | undefined;
  productRepositoryId: string | undefined;
};

const VEHICLE_FITMENT_ERROR_CODES = ['3076', '3074', '3070'];

const DealDrawerComponent = ({
  analyticsPageType,
  setShowYmme,
  showYmme,
  open,
  skuId,
  skuQuantity,
  skuPrice,
  skuRetailPrice,
  skuFulfillmentTypeId,
  closeDealsDrawer,
  skuIsVehicleSpecific,
  handleDealsAddToCart,
  dealsAddedToCart,
  brand,
  onContinueShopping,
  dealConfigurableLabel,
  fittingPartsURL,
  //Aces properties
  partGroupId,
  partTerminologyId,
  //Non Aces properties

  partTypeId,
  productRepositoryId,
}: Props) => {
  const dispatch = useDispatch();
  const labels = useLabels(labelMap);
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: pageTypeData } = usePageType();
  const miniCartModalRef = useRef<HTMLDivElement>(null);
  const [groupId, setGroupId] = useState<string | null>(String(skuFulfillmentTypeId));
  const [fulfillmentId, setFulfillmentId] = useState<number | undefined>();
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [confirmSelectedDeal, setConfirmSelectedDeal] = useState(false);
  const [addedToCart, setAddedToCart] = useState<boolean | null>(dealsAddedToCart ?? false);
  const [needsVehicleSelection, setNeedsVehicleSelection] = useState(false);

  const { acesEnabled } = useAcesFlags();

  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);
  const preferredVehicle = usePreferredVehicle();
  const preferredVehicleId = preferredVehicle?.catalogVehicleId;
  const dealDrawerFulfillmentOptionsEnabled =
    useFeatureFlag('DEAL_FULFILLMENT_OPTIONS_ENABLED') === 'true';
  const makeModelYearPageType = pageTypeData?.makeModelYearPath;
  const [miniCartDealData, setminiCartDealData] = useState<Set<string>>(new Set());
  const removeData = () => dispatch(removeDataPart());
  const { data: headerData } = useHeaderData();
  const miniCartMap = headerData?.miniCartMap;
  const cartSuccessData = useSelector(({ shelf }: ReduxState) => shelf.cartSuccess);
  const isDealsDrawerMiniCartEnabled =
    useFeatureFlag('IS_DEALS_DRAWER_MINI_CART_ENABLED') === 'true';
  const [shouldOpenDrawerAfterVehicleSelection, setShouldOpenDrawerAfterVehicleSelection] =
    useState(false);
  const [showDealAddedToast, setShowDealAddedToast] = useState(false);

  const submitToCart = () => {
    void router.push(routePaths.cart);
  };

  const calculateVehicleSelectionNeed = useCallback(() => {
    let vehicleSelectionIsNeed = false;
    const skuSpecific = skuIsVehicleSpecific || false;

    if (makeModelYearPageType && !preferredVehicle) {
      vehicleSelectionIsNeed = true;
    } else {
      vehicleSelectionIsNeed = skuSpecific && !preferredVehicle;
    }

    return vehicleSelectionIsNeed;
  }, [makeModelYearPageType, preferredVehicle, skuIsVehicleSpecific]);

  useEffect(() => {
    setNeedsVehicleSelection(calculateVehicleSelectionNeed());
  }, [calculateVehicleSelectionNeed]);

  const queryEnabled = open && !needsVehicleSelection;
  const dealListResult = useDealList({
    enabled: queryEnabled,
    skuId,
  });

  const skuDetailsResult = useDealSkuDetails({
    enabled: queryEnabled,
    skuIds: skuId ? [skuId] : [],
  });
  const deals = dealListResult.data?.deals;
  const parentSkuDetails = skuId ? skuDetailsResult.data?.skus[skuId] : undefined;

  const error = dealListResult.error ?? skuDetailsResult.error;
  const errorCode = getErrorCode(error);
  const errorCodeDescription = getErrorCodeDescription(error);

  useEffect(() => {
    if (open) {
      return;
    }

    queryClient.removeQueries({
      queryKey: dealListQuery.getFullKey({
        skuId,
        apiVersion: acesEnabled ? 'v4' : 'v3',
        dealsVehicleAttributes,
      }),
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: skuDealDetailsQuery.getFullKey({ skuIds: skuId ? [skuId] : [] }),
      exact: true,
    });
    setSelectedDeal(null);
    setConfirmSelectedDeal(false);
    setAddedToCart(null);
    setGroupId(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === eventConstants.escKeyCode) {
        closeDealsDrawer();
      }
    };

    document.addEventListener('keyup', handleKeyDown);
    return () => {
      document.removeEventListener('keyup', handleKeyDown);
    };
  }, [open, closeDealsDrawer]);

  useEffect(() => {
    setGroupId(String(skuFulfillmentTypeId));
  }, [skuFulfillmentTypeId]);

  const lineItem = orderDetailsFromState.orderItems
    .flatMap((orderItem) => orderItem.shipmentInfoList)
    .flatMap((shipment) => shipment.lineItemList)
    .find((lineItem) => lineItem.productInfo.skuId === skuId);

  const shelfPageUrl =
    lineItem?.productInfo.shelfPageUrl && lineItem.productInfo.shelfPageUrl !== '/'
      ? lineItem.productInfo.shelfPageUrl
      : (fittingPartsURL ?? '/');

  const currentPathIsCart = legacyRouter.pathname === routePaths.cart;

  const handleClose = () => {
    if (showYmme) {
      setShowYmme(false);
      return;
    }
    dispatch(resetDiscounts());
    if (addedToCart) {
      removeData();
      setminiCartDealData(new Set());
      if (onContinueShopping) {
        onContinueShopping();
      }
    }
    closeDealsDrawer();
  };
  const handleBack = () => {
    setConfirmSelectedDeal(false);
    setAddedToCart(false);
    setSelectedDeal(null);

    setGroupId(null);
    setFulfillmentId(undefined);
  };

  const handleAutoFulfillmentSuccess = (groupTypeId: string, fulfillmentIdParam?: number) => {
    setGroupId(groupTypeId);
    setFulfillmentId(fulfillmentIdParam ?? undefined);
    setConfirmSelectedDeal(true);
  };

  const handleAutoFulfillmentFailure = () => {
    setSelectedDeal(null);
    setGroupId(null);
    setFulfillmentId(undefined);
  };

  const handleContinueShopping = () => {
    handleClose();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleInstockAnalytics = () => {
    if (dealDrawerFulfillmentOptionsEnabled) {
      trackOnDealInStockNearSelection(selectedDeal);
    }
  };

  const onDealSelection = (deal: Deal) => {
    if (!dealDrawerFulfillmentOptionsEnabled && !skuId) {
      setConfirmSelectedDeal(true);
      setGroupId(groupId);
    }

    setSelectedDeal(deal);
  };

  const handlePostCartAddNavigation = () => {
    setAddedToCart(true);
  };

  const onAddedToCart = () => {
    if (currentPathIsCart) {
      dispatch(resetDiscounts());
      closeDealsDrawer();
    } else {
      handlePostCartAddNavigation();

      if (handleDealsAddToCart) {
        handleDealsAddToCart();
      }
    }
  };

  const trackOnDealSelection = (dealInfo: {
    dealId: string;
    name: string | null;
    description: string | null;
  }) => {
    const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
    const dealDetailsList: CustomLinkName = 'Deal Details List';
    const data = {
      eventType: 'dealsBuildBundle',
      dealId: dealInfo.dealId,
      dealName: dealInfo.name,
      dealDescription: dealInfo.description,
      pageName,
      pageType,
      siteSection,
      customLinkName: `${pageType}:${dealDetailsList}`,
    };
    clickTrack(data);
  };

  const trackOnDealInStockNearSelection = (dealInfo: Deal | null) => {
    const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
    let dealEventType = '';

    if (pageType === 'cart') {
      dealEventType = eventTypes.findAnotherStoreCart;
    }

    if (pageType === 'catalog_shelf') {
      dealEventType = eventTypes.findAnotherStoreShelf;
    }

    if (pageType === 'catalog_product-detail') {
      trackInStockNearYouLinkClick();
    }

    if (!dealEventType && pageType !== 'catalog_product-detail') {
      return;
    }

    const inStockNearYou: CustomLinkName = 'In Stock Near You';
    const data = {
      eventType: dealEventType,
      dealId: dealInfo?.id,
      dealName: dealInfo?.name,
      pageName,
      pageType,
      siteSection,
      customLinkName: `${pageType}:${inStockNearYou}`,
    };
    clickTrack(data);
  };

  const trackOnDealsAvailableLink = useCallback(
    (dealDrawerInfo: {
      dealIdListString: string;
      dealNameListString: string;
      dealDescriptionListString: string;
    }) => {
      const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
      const dealAvailableList: CustomLinkName = 'Deal Available List';
      const data = {
        eventType: 'dealsAvailableLink',
        dealId: dealDrawerInfo.dealIdListString,
        dealName: dealDrawerInfo.dealNameListString,
        dealDescription: dealDrawerInfo.dealDescriptionListString,
        pageName,
        pageType,
        siteSection,
        customLinkName: `${pageType}:${dealAvailableList}`,
        productIdList: [skuId],
        ...(acesEnabled
          ? {
              partGroupId: [partGroupId],
              partTerminologyId: [partTerminologyId],
            }
          : {
              originalPartTypeIdList: [partTypeId],
              productRepositoryIdList: [productRepositoryId],
            }),
        productBrandList: [brand],
        productPriceList: [skuRetailPrice.toString().replace(',', '.')],
        productDealMessage: [dealConfigurableLabel ? dealConfigurableLabel : 'See Available Deals'],
      };
      clickTrack(data);
    },
    [
      skuId,
      acesEnabled,
      partGroupId,
      partTerminologyId,
      partTypeId,
      productRepositoryId,
      brand,
      skuRetailPrice,
      dealConfigurableLabel,
    ],
  );

  const handleDoesNotFitOnClick = () => {
    if (!isServer && window.utag_data) {
      clickTrack({
        customLinkName: `${window.utag_data.pageType} - Choose Another Deal Item`,
        eventType: 'choose_another_deal_item',
        pageName: window.utag_data.pageName,
        pageType: window.utag_data.pageType,
        siteSection: window.utag_data.siteSection,
      });
    }

    closeDealsDrawer();
  };

  const trackOnDealsError = (err: string, errCode: string) => {
    const {
      preferredVehicleEngine = '',
      preferredVehicleId = '',
      preferredVehicleMake = '',
      preferredVehicleModel = '',
      preferredVehicleYear = '',
      preferredStoreId = '',
      pageType = '',
    } = root.utag_data || {};
    const dealDrawerError: CustomLinkName = 'Deal Drawer Error';
    const data = {
      customLinkName: `${pageType}:${dealDrawerError}`,
      eventType: eventTypes.dealDrawerError,
      errorMsg: err,
      errorCode: errCode,
      errorMsgType: dealDrawerError,
      productIdList: [skuId],
      preferredVehicleEngine,
      preferredVehicleId,
      preferredVehicleMake,
      preferredVehicleModel,
      preferredVehicleYear,
      preferredStoreId,
    };
    clickTrack(data);
  };

  useEffect(() => {
    if (errorCode && errorCodeDescription) {
      trackOnDealsError(errorCodeDescription, errorCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode, errorCodeDescription]);

  useEffect(() => {
    if (shouldOpenDrawerAfterVehicleSelection && preferredVehicle) {
      setShouldOpenDrawerAfterVehicleSelection(false);
    } else if (shouldOpenDrawerAfterVehicleSelection && !preferredVehicle) {
      setShouldOpenDrawerAfterVehicleSelection(false);
      closeDealsDrawer();
    }
  }, [shouldOpenDrawerAfterVehicleSelection, preferredVehicle, closeDealsDrawer]);

  const handleVehicleSelectionClose = (vehicleWasSelected?: boolean) => {
    if (vehicleWasSelected) {
      setShowYmme(false);
    } else {
      setShowYmme(false);
      setShouldOpenDrawerAfterVehicleSelection(false);
      closeDealsDrawer();
    }
  };

  if (needsVehicleSelection && showYmme && acesEnabled) {
    return (
      <div className={styles.vehicleSelectionContainer}>
        <BundleDealVehicleSelection onClose={handleVehicleSelectionClose} showYmme={showYmme} />
      </div>
    );
  }

  let content;
  let ariaLabelledBy;

  if (
    (dealListResult.isError || skuDetailsResult.isError) &&
    (acesEnabled ? !needsVehicleSelection : true)
  ) {
    content = (
      <>
        {VEHICLE_FITMENT_ERROR_CODES.includes(errorCode ?? '') && parentSkuDetails ? (
          shelfPageUrl === '/' ? (
            <DealsLoader showCloseButton handleClose={closeDealsDrawer} />
          ) : (
            <>
              <NavBar
                showCloseButton
                handleClose={closeDealsDrawer}
                handleBack={closeDealsDrawer}
              />
              <VehicleNotFit
                fittingPartsURL={shelfPageUrl}
                parentSkuCardProps={{
                  skuDetails: parentSkuDetails,
                  skuQuantity: skuQuantity,
                  skuPrice: skuPrice,
                  skuRetailPrice: skuRetailPrice,
                }}
                onVehicleDoesNotFitCtaOnClick={handleDoesNotFitOnClick}
              />
            </>
          )
        ) : (
          <div className={styles.errorContainer}>
            <NotificationBlock
              type="error"
              message={errorCodeDescription || labels.label_deal_bundle_unexpected_error}
            />
          </div>
        )}
      </>
    );
  } else if (
    (dealListResult.isPending && dealListResult.isFetching) ||
    (skuDetailsResult.isPending && skuDetailsResult.isFetching)
  ) {
    content = <DealsLoader showCloseButton handleClose={closeDealsDrawer} />;
  } else if (needsVehicleSelection && !acesEnabled) {
    ariaLabelledBy = 'dealsModal';
    content = (
      <div className={styles.vehicleSelectionContainer}>
        <BundleDealVehicleSelection
          onClose={handleClose}
          setShowYmme={setShowYmme}
          showYmme={showYmme}
        />
      </div>
    );
  } else if (addedToCart) {
    if (!isDealsDrawerMiniCartEnabled) {
      content = (
        <DealDrawerConfirmation
          onClose={handleClose}
          onBackClick={handleBack}
          onContinueShopping={handleContinueShopping}
        />
      );
    } else {
      content = (
        <MiniCartFlyoutComp
          handleClose={handleClose}
          isModalOpen={addedToCart}
          image={parentSkuDetails?.productImageUrl ?? ''}
          successData={cartSuccessData}
          productDetails={{}}
          miniCartData={miniCartMap}
          closeStoreModal={handleClose}
          cartUrl={submitToCart}
          productTitle={selectedDeal?.name ?? ''}
          pageName="pdp"
          orderDetailsFromState={orderDetailsFromState}
          miniCartDealData={miniCartDealData}
          parentRef={miniCartModalRef}
          partGroupId={partGroupId}
        />
      );
      ariaLabelledBy = MINI_CART_TITLE_ID;
    }
  } else {
    if (
      confirmSelectedDeal &&
      parentSkuDetails &&
      (groupId || skuFulfillmentTypeId) &&
      selectedDeal
    ) {
      content = (
        <BundleDealV2
          analyticsPageType={analyticsPageType}
          deal={selectedDeal}
          parentSkuDetails={parentSkuDetails}
          skuFulfillmentTypeId={fulfillmentId ?? skuFulfillmentTypeId ?? groupId}
          skuIsVehicleSpecific={skuIsVehicleSpecific}
          onAddedToCart={onAddedToCart}
          onClose={handleClose}
          onBackClick={handleBack}
          trackOnDealSelection={trackOnDealSelection}
          handleErrorAnalytics={trackOnDealsError}
          dealItemSkuSet={miniCartDealData}
          setminiCartDealData={setminiCartDealData}
        />
      );
    } else if (
      selectedDeal &&
      parentSkuDetails &&
      (groupId || skuFulfillmentTypeId) &&
      dealDrawerFulfillmentOptionsEnabled
    ) {
      content = (
        <AutoFulfillmentSelector
          dealId={selectedDeal.id}
          preferredVehicleId={preferredVehicleId}
          parentSkuDetails={parentSkuDetails}
          onSuccess={handleAutoFulfillmentSuccess}
          onFailure={handleAutoFulfillmentFailure}
          onClose={handleClose}
          onBack={handleBack}
          handleErrorAnalytics={trackOnDealsError}
        />
      );
    } else {
      if (deals && parentSkuDetails) {
        ariaLabelledBy = 'deal-drawer-title';
        content = (
          <BundleDealOptionList
            parentSkuDetails={parentSkuDetails}
            skuQuantity={skuQuantity}
            skuPrice={skuPrice}
            skuRetailPrice={skuRetailPrice}
            deals={deals}
            trackOnDealsAvailableLink={trackOnDealsAvailableLink}
            onSelect={onDealSelection}
            onClose={handleClose}
            onBackClick={handleBack}
            skuIsVehicleSpecific={skuIsVehicleSpecific}
          />
        );
      }
    }
  }

  return (
    <>
      <Drawer
        data-testid="deals-bundle-drawer"
        anchor={'right'}
        open={open}
        PaperProps={{
          className: styles.drawer,
        }}
        onClose={handleClose}
        drawerRef={miniCartModalRef}
      >
        <TrapFocus open={open} shouldFocus>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            className={styles.bundleDrawerBody}
          >
            {content}
          </div>
        </TrapFocus>
      </Drawer>
      <AZSnackBar
        open={showDealAddedToast}
        message={labels.lblDealAddedToCart}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          setShowDealAddedToast(false);
        }}
      />
    </>
  );
};

export const DealDrawer = memo(DealDrawerComponent);
